import React from 'react';
import { ClickableContainer, ExternalLinkContainer, InternalLinkContainer } from './Clickable.styles';
import { urlIsExternal } from 'src/functions/propagateQueryParams';
import useBaseLink from 'src/hooks/useBaseLink';

export interface ClickableProps {
  children?: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => void;
  disabled?: boolean;
  href?: string;
  className?: string;
  target?: string;
  rel?: string;
  display?: 'inline-block' | 'block' | 'flex';
  dataCy?: string;
  'data-track-id'?: string;
  'data-track-type'?: string;
}

const Clickable = (props: ClickableProps): JSX.Element => {
  const { children, href, disabled, className, target, rel, display, dataCy, ...rest } = props;

  if (href) {
    const linkProps = useBaseLink(href, rest.onClick);

    if (urlIsExternal(href)) {
      return (
        <ExternalLinkContainer
          href={linkProps.href}
          onClick={linkProps.onClick}
          target={target || '_blank'}
          rel={rel || 'noopener noreferrer'}
          $disabled={disabled}
          $display={display}
          className={className}
          data-cy={dataCy}
          {...rest}
        >
          {children}
        </ExternalLinkContainer>
      );
    }

    return (
      <InternalLinkContainer
        {...linkProps}
        to={linkProps.href}
        onClick={linkProps.onClick}
        $disabled={disabled}
        $display={display}
        className={className}
        data-cy={dataCy}
        {...rest}
      >
        {children}
      </InternalLinkContainer>
    );
  }

  const bypass = !href && !rest.onClick;

  return (
    <ClickableContainer
      $disabled={disabled}
      $bypass={bypass}
      $display={display}
      className={className}
      data-cy={dataCy}
      {...rest}
    >
      {children}
    </ClickableContainer>
  );
};

export default Clickable;
